import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { getListing, updateListingStatus, joinQueue, leaveQueue, getUserQueueEntry, removeFromQueue } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = {
  available: 'Available',
  deposit_paid: 'Deposit Paid',
  sold: 'Sold',
};

const STATUS_COLORS = {
  available:    { border: '#7A9E7E', color: '#7A9E7E' },
  deposit_paid: { border: '#C9A84C', color: '#C9A84C' },
  sold:         { border: '#888',    color: '#888' },
};

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [myEntry, setMyEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerLoading, setOfferLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  const isSeller = user && listing && user.id === listing.user_id;
  const sellerQr = listing?.profiles?.payment_qr_url;
  const sellerTelegram = listing?.profiles?.telegram_number;

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const load = async () => {
    try {
      const data = await getListing(id);
      setListing(data);
      if (user && data) {
        const entry = await getUserQueueEntry(id, user.id);
        setMyEntry(entry);
      }
    } catch (e) {
      setError('Listing not found.');
    } finally {
      setLoading(false);
    }
  };

  const handleOffer = async () => {
    if (!user) return navigate('/login');
    setOfferLoading(true);
    setError(''); setSuccess('');
    try {
      if (myEntry) {
        await leaveQueue(id, user.id);
        setSuccess('Your offer has been withdrawn.');
        setMyEntry(null);
      } else {
        await joinQueue(id, user.id);
        setSuccess('Your offer to buy has been submitted!');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setOfferLoading(false);
    }
  };

  const handleRemoveOffer = async (entryId, username) => {
    if (!window.confirm('Remove ' + username + "'s offer?")) return;
    try {
      await removeFromQueue(entryId);
      setListing(prev => ({
        ...prev,
        queue_entries: prev.queue_entries.filter(e => e.id !== entryId)
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleStatus = async (status) => {
    setStatusLoading(true);
    setError('');
    try {
      await updateListingStatus(id, status);
      setListing(prev => ({ ...prev, status }));
    } catch (e) {
      setError(e.message);
    } finally {
      setStatusLoading(false);
    }
  };

  const getTelegramLink = (val) => {
    if (!val) return null;
    const clean = val.trim();
    if (clean.startsWith('@')) return `https://t.me/${clean.slice(1)}`;
    if (clean.startsWith('http')) return clean;
    const digits = clean.replace(/[^0-9]/g, '');
    if (digits.length > 5) return `https://t.me/+${digits}`;
    return `https://t.me/${clean}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;
  if (!listing) return <div className="page"><div className="alert alert-e">Listing not found.</div></div>;

  const offers = listing.queue_entries || [];
  const telegramLink = getTelegramLink(sellerTelegram);

  return (
    <div className="page">
      <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-g btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <button className="btn btn-g btn-sm" onClick={copyLink} style={{ marginLeft: 'auto' }}>
          {copied ? '✓ Link Copied!' : '🔗 Share Listing'}
        </button>
      </div>

      {error && <div className="alert alert-e">{error}</div>}
      {success && <div className="alert alert-s">{success}</div>}

      <div className="detail-grid">
        <div>
          {listing.image_url
            ? <img className="detail-img" src={listing.image_url} alt={listing.title} />
            : <div className="detail-img-ph">🛍️</div>
          }
        </div>

        <div>
          <span className={`badge b-${listing.status}`}>{STATUS_LABELS[listing.status] || listing.status}</span>
          <h1 style={{ fontFamily: 'var(--font-d)', fontSize: '2rem', fontWeight: 900, marginTop: '.5rem', letterSpacing: '-1px' }}>
            {listing.title}
          </h1>
          <div className="detail-price">${Number(listing.price).toFixed(2)}</div>

          <div className="info-box">
            <div className="info-row">
              <span className="info-label">Seller</span>
              <span
                style={{ color: 'var(--terracotta)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate(`/seller/${listing.profiles?.username}`)}
              >{listing.profiles?.username || '—'}</span>
            </div>
            {listing.category && (
              <div className="info-row">
                <span className="info-label">Category</span>
                <span>{listing.category}</span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Listed</span>
              <span>{format(new Date(listing.created_at), 'MMM d, yyyy')}</span>
            </div>
            {listing.available_date && (
              <div className="info-row">
                <span className="info-label">Available</span>
                <span>{format(new Date(listing.available_date), 'MMM d, yyyy')}</span>
              </div>
            )}
            {listing.status === 'deposit_paid' && listing.deposit_amount != null && (
              <div className="info-row">
                <span className="info-label">Deposit Paid</span>
                <span style={{ color: '#8A6800', fontWeight: 700 }}>${Number(listing.deposit_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Offers to Buy</span>
              <span>{offers.length} {offers.length === 1 ? 'person' : 'people'}</span>
            </div>
          </div>

          {/* ASK THE SELLER */}
          {!isSeller && telegramLink && (
            <div style={{ marginBottom: '1.25rem' }}>
              <a
                href={telegramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sage btn-full"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem', textDecoration: 'none' }}
              >
                <span style={{ fontSize: '1.1rem' }}>✈️</span>
                Ask the Seller on Telegram
              </a>
              <div style={{ fontSize: '.75rem', color: 'var(--brown)', textAlign: 'center', marginTop: '.35rem' }}>
                Opens Telegram to chat with {listing.profiles?.username}
              </div>
            </div>
          )}

          {!isSeller && !telegramLink && (
            <div style={{ marginBottom: '1.25rem', padding: '.75rem 1rem', background: 'var(--cream)', borderRadius: 'var(--r)', fontSize: '.85rem', color: 'var(--brown)' }}>
              💬 Contact seller directly to ask questions about this item.
            </div>
          )}

          {isSeller && !sellerTelegram && (
            <div className="alert alert-i" style={{ marginBottom: '1.25rem' }}>
              Add your Telegram in <button
                style={{ background: 'none', border: 'none', color: 'var(--terracotta)', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit' }}
                onClick={() => navigate('/dashboard?tab=profile')}>
                Dashboard → Profile
              </button> so buyers can contact you.
            </div>
          )}

          {/* SELLER STATUS CONTROLS */}
          {isSeller && (
            <div style={{ marginBottom: '1.25rem' }}>
              <div className="section-title">Update Status</div>
              <div className="status-btns">
                {Object.entries(STATUS_LABELS).map(([v, l]) => {
                  const active = listing.status === v;
                  const col = STATUS_COLORS[v];
                  return (
                    <button key={v} className="s-btn" disabled={statusLoading} onClick={() => handleStatus(v)}
                      style={{ borderColor: col.border, color: active ? '#fff' : col.color, background: active ? col.border : 'transparent' }}>
                      {l}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: '.75rem', display: 'flex', gap: '.5rem' }}>
                <button className="btn btn-g btn-sm" onClick={() => navigate('/dashboard')}>← My Listings</button>
                <button className="btn btn-s btn-sm" onClick={() => navigate(`/listing/${id}/edit`)}>✏ Edit Listing</button>
              </div>
            </div>
          )}

          {/* OFFER TO BUY */}
          {!isSeller && listing.status !== 'sold' && (
            <div style={{ marginBottom: '1.25rem' }}>
              {myEntry ? (
                <>
                  <div className="alert alert-s" style={{ marginBottom: '.75rem' }}>
                    ✅ You are #{myEntry.position} on the offers list.
                  </div>
                  <button className="btn btn-g" onClick={handleOffer} disabled={offerLoading}>
                    {offerLoading ? 'Withdrawing…' : 'Withdraw My Offer'}
                  </button>
                </>
              ) : (
                <button className="btn btn-p btn-full" onClick={handleOffer} disabled={offerLoading}>
                  {offerLoading ? 'Submitting…' : user ? '🙋 Offer to Buy' : 'Sign In to Make an Offer'}
                </button>
              )}
            </div>
          )}

          {/* PAYMENT QR */}
          {sellerQr && listing.status !== 'sold' && (
            <div className="qr-box">
              <h3>Pay the Seller</h3>
              <p>Scan this QR code to send a deposit or payment directly to the seller outside the site.</p>
              <div style={{ display: 'inline-block', padding: '.75rem', background: '#fff', border: '1px solid var(--border)', borderRadius: 8, marginBottom: '.75rem' }}>
                <QRCodeSVG value={sellerQr} size={160} />
              </div>
              <div style={{ fontSize: '.78rem', color: 'var(--brown)' }}>Payment handled externally — confirm with seller after paying.</div>
            </div>
          )}

          {!sellerQr && !isSeller && listing.status !== 'sold' && (
            <div className="qr-box" style={{ borderStyle: 'solid' }}>
              <p style={{ marginBottom: 0 }}>💬 Contact the seller to arrange payment details.</p>
            </div>
          )}

          {isSeller && !sellerQr && (
            <div className="alert alert-i">
              Add your payment QR in <button
                style={{ background: 'none', border: 'none', color: 'var(--terracotta)', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 'inherit' }}
                onClick={() => navigate('/dashboard?tab=profile')}>
                Dashboard → Profile
              </button> so buyers can pay you.
            </div>
          )}
        </div>
      </div>

      {/* OFFERS LIST */}
      {offers.length > 0 && (
        <div style={{ marginTop: '3rem', maxWidth: 480 }}>
          <div className="section-title">
            {isSeller ? `People Who Want to Buy (${offers.length})` : `Offers to Buy (${offers.length})`}
          </div>
          <ul className="queue-list">
            {offers.sort((a, b) => a.position - b.position).map(entry => {
              const buyerTelegram = entry.profiles?.telegram_number;
              const buyerLink = buyerTelegram
                ? buyerTelegram.startsWith('@')
                  ? 'https://t.me/' + buyerTelegram.slice(1)
                  : 'https://t.me/+' + buyerTelegram.replace(/[^0-9]/g, '')
                : null;
              return (
                <li key={entry.id} className="q-item">
                  <span className="q-pos">{entry.position}</span>
                  <span style={{ fontWeight: 500 }}>{entry.profiles?.username || 'User'}</span>
                  {user && entry.user_id === user.id && <span className="q-you">YOU</span>}

                  {isSeller && (
                    <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.3rem' }}>
                      {buyerLink
                        ? <a href={buyerLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '.75rem', background: 'var(--sage)', color: '#fff', padding: '.2rem .6rem', borderRadius: 12, textDecoration: 'none', fontWeight: 600 }}>✈️ Telegram</a>
                        : <span style={{ fontSize: '.72rem', color: 'var(--brown)', fontStyle: 'italic' }}>no Telegram</span>
                      }
                      {entry.profiles?.email
                        ? <a href={'mailto:' + entry.profiles.email} style={{ fontSize: '.72rem', color: 'var(--terracotta)', textDecoration: 'none', fontWeight: 500 }}>✉️ {entry.profiles.email}</a>
                        : <span style={{ fontSize: '.72rem', color: 'var(--brown)', fontStyle: 'italic' }}>no email</span>
                      }
                      <button
                        onClick={() => handleRemoveOffer(entry.id, entry.profiles?.username || 'this user')}
                        style={{ fontSize: '.7rem', background: 'transparent', border: '1px solid #FFCDD2', color: '#C62828', padding: '.15rem .5rem', borderRadius: 10, cursor: 'pointer', fontWeight: 600, marginTop: '.1rem' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#FFEBEE'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >✕ Remove offer</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
